import type { ApplicationContext } from '@hc-frontend/core-context';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import {
  LeadStatus,
  UpdateProspectAnalyticsIdentifierParameters,
} from '../../entities';
import type {
  UpdateProspectAnalyticsIdentifierRepository,
  UpdateProspectAnalyticsIdentifierResult,
} from '../../repositories';
import type { GetProspectUseCase } from '../get-prospect/get-prospect';

export type UpdateProspectAnalyticsIdentifierUseCase = (
  product: UpdateProspectAnalyticsIdentifierParameters,
) => Promise<UpdateProspectAnalyticsIdentifierResult>;

export type UpdateProspectAnalyticsIdentifierFactoryParams = Pick<
  ApplicationContext,
  'log'
> & {
  updateProspectAnalyticsIdentifier: UpdateProspectAnalyticsIdentifierRepository;
  getProspect: GetProspectUseCase;
};

export function updateProspectAnalyticsIdentifierFactory({
  updateProspectAnalyticsIdentifier,
  log,
  getProspect,
}: UpdateProspectAnalyticsIdentifierFactoryParams): UpdateProspectAnalyticsIdentifierUseCase {
  return async (parameters) => {
    const currentProspect = await getProspect(
      parameters.sessionId,
      parameters.userId,
      parameters.domainSessionId,
      parameters.domainUserId,
      LeadStatus.Enum.PROSPECT,
    );

    if (!currentProspect.success) {
      const lead = await getProspect(
        parameters.sessionId,
        parameters.userId,
        parameters.domainSessionId,
        parameters.domainUserId,
      );

      if (!lead.success) {
        log({
          type: 'error',
          context: 'server',
          message: lead.errors[0].code,
          data: {
            rootCause: lead.errors[0].rootCause,
          },
        });
      }
      return createSuccessOperationResult(undefined);
    }

    if (currentProspect.data.sold)
      return createSuccessOperationResult(undefined);

    const parsedParameters =
      UpdateProspectAnalyticsIdentifierParameters.safeParse(parameters);

    if (!parsedParameters.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToUpdateProspectAnalyticsIdentifier',
        data: {
          rootCause: parsedParameters.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectAnalyticsIdentifier',
            rootCause: parsedParameters.error,
          },
        ],
      });
    }

    const result = await updateProspectAnalyticsIdentifier(parameters);

    if (!result.success) {
      log({
        context: 'server',
        type: 'error',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
    }

    return result;
  };
}
